import { useContext, useEffect, useState } from "react";
import SettingsContainer from "./SettingsContainer";
import { ErrorAlert, SuccessAlert } from "../../components/alerts";
import {
  createKioskAttractor,
  getSingleUploadPresignedS3Url,
  uploadToS3PresignedUrl,
} from "../../api/new-api";
import { useNavigate } from "react-router";
import { PELinearProgressWithLabel } from "../../components/linear-progress";
import UploadImageContainer from "../../components/UploadImageContainer";
import { UserStore } from "../..";
import { observer } from "mobx-react";
import KioskAttractorDisplayPreview from "../../components/KioskAttractorDisplayPreview";

// Simple CSS for the loader animation and media
const loaderStyles = `
  .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 20px auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .success-checkmark {
    display: inline-block;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: #28a745;
    position: relative;
    margin: 20px auto;
  }

  .success-checkmark::before {
    content: "";
    position: absolute;
    top: 15px;
    left: 20px;
    width: 10px;
    height: 20px;
    border: solid white;
    border-width: 0 4px 4px 0;
    transform: rotate(45deg);
  }

  .media-preview {
    max-width: 300px;
    width: 100%;
    height: auto;
    margin: 20px 0;
    border-radius: 8px;
  }
`;

const AttractorCreator = () => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;

  const [isVideoAllowed, setIsVideoAllowed] = useState(
    selectedVenue?.venueFeatures?.videoAttractorsEnabled ?? false
  );
  const [file, setFile] = useState<null | File>(null);
  const [fileType, setFileType] = useState<null | string>(null);
  const [blobUrl, setBlobUrl] = useState<null | string>(null);
  const [orientation, setOrientation] = useState<any>(null);
  const [error, setError] = useState<any>("");
  const [percentCompleted, setPercentCompleted] = useState(0);

  // New state to manage screen
  const [screenState, setScreenState] = useState<
    "form" | "loading" | "success" | "error"
  >("form");

  const handleFileChange = (event: { target: { files: any[] } }) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
      setError("");

      const fileType = file.type.includes("mp4") ? "video" : "image";
      setFileType(fileType);

      if (fileType === "video") {
        const videoUrl = URL.createObjectURL(file);
        setBlobUrl(videoUrl);

        const video = document.createElement("video");
        video.src = videoUrl;
        video.onloadedmetadata = () => {
          if (video.videoWidth > video.videoHeight) {
            setOrientation("landscape");
          } else {
            setOrientation("portrait");
          }
        };
      }

      if (fileType === "image") {
        const blobUrl = URL.createObjectURL(file);
        setBlobUrl(blobUrl);

        const img = new Image();
        img.src = blobUrl;
        img.onload = () => {
          if (img.width > img.height) {
            setOrientation("landscape");
          } else {
            setOrientation("portrait");
          }
        };
      }
    }
  };

  const validate = (startDate: any, endDate: any, selected: any) => {
    if (orientation === "landscape") {
      setError("Please upload a portrait image.");
      return true;
    }
    if (!file) {
      setError("Please upload a file");
      return true;
    }
    if (!startDate) {
      setError("Please select a start date");
      return true;
    }
    if (!endDate) {
      setError("Please select an end date");
      return true;
    }
    if (startDate > endDate) {
      setError("Start date must be before end date");
      return true;
    }
    if (new Date(startDate).getTime() === new Date(endDate).getTime()) {
      setError("Start date and end date cannot be the same");
      return true;
    }
    if (!selected || selected?.length === 0) {
      setError("Please select a day visibility");
      return true;
    }
    setError("");
    return false;
  };

  const handleSubmit = async ({ startDate, endDate, selected }: any) => {
    const hasErrors = validate(startDate, endDate, selected);
    if (hasErrors) {
      return;
    }

    try {
      setScreenState("loading");
      setPercentCompleted(2);

      const presignedUrlResponse = await getSingleUploadPresignedS3Url({
        imageFileName: file!.name,
      });

      const uploadUrl = presignedUrlResponse.data.imageUploadUrl;
      const mediaS3Key = presignedUrlResponse.data.imageKey;

      await uploadToS3PresignedUrl({
        uploadUrl,
        data: file,
        progressCallback: (percentCompleted) => {
          console.log(`Upload progress: ${percentCompleted}%`);
          setPercentCompleted(percentCompleted);
        },
      });

      const attractor = {
        startDateEpoch: new Date(startDate).getTime(),
        endDateEpoch: new Date(endDate).getTime(),
        dayVisibility: selected.map((val: any) => val.value).join(","),
      };

      await createKioskAttractor({
        mediaS3Key,
        attractor,
      });

      setPercentCompleted(100);
      setScreenState("success");
      setTimeout(() => {
        navigate("/kiosk-attractors");
      }, 500); // 0.5-second delay
    } catch (error) {
      console.error("An error occurred:", error);
      setScreenState("error");
      setError("An error occurred. Please try again or cancel.");
    }
  };

  useEffect(() => {
    setFile(null);
    setBlobUrl(null);
    if (selectedVenue) {
      setIsVideoAllowed(
        selectedVenue?.venueFeatures?.videoAttractorsEnabled ?? false
      );
    }
  }, [selectedVenue]);

  // Render the form screen
  const renderForm = () => (
    <div className="w-full">
      <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
        <div className="flex flex-row gap-2 justify-center">
          <div className="w-full flex justify-center">
            <KioskAttractorDisplayPreview
              currentAttractors={blobUrl ? [{ imageUrl: blobUrl }] : []}
              blobIsVideo={fileType === "video"}
            />
          </div>
          <div className="col-span-full w-full">
            <UploadImageContainer
              id="thumbnailBlobUrl"
              label={`Upload ${isVideoAllowed ? "Video or Image" : "Image"}`}
              blobUrl={blobUrl}
              errorMessage={error}
              handleFileChange={handleFileChange}
              description={`Width to Height Ratio of 2:1. example: 1080 width x 1920 height`}
              fileType={fileType}
              allowVideo={isVideoAllowed}
            />
            <div>
              <SettingsContainer
                data={
                  {
                    startDateEpoch: null,
                    endDateEpoch: null,
                    dayVisibility: null,
                  } as any
                }
                openSettings={true}
                setOpenSettings={() => {}}
                hideCloseButton={false}
                hideDeleteButton={true}
                submitText="Create"
                cancelText="Cancel"
                handleSubmit={handleSubmit}
                handleDelete={() => {}}
                handleClose={() => navigate("/kiosk-attractors")}
              />
            </div>
            {orientation === "landscape" && (
              <div className="mt-2">
                <ErrorAlert message="Please upload a portrait image." />
              </div>
            )}
            {error && !orientation && (
              <div className="mt-2">
                <ErrorAlert message={error} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render the loading screen with blobUrl as image or video
  const renderLoading = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      <div className="loader" />
      <p className="text-lg font-semibold mb-4">Creating Kiosk Attractor</p>
      {blobUrl && fileType === "image" && (
        <img src={blobUrl} alt="Media Preview" className="media-preview" />
      )}
      {blobUrl && fileType === "video" && (
        <video src={blobUrl} className="media-preview" autoPlay muted loop />
      )}
      <PELinearProgressWithLabel value={percentCompleted} />
    </div>
  );

  // Render the success screen
  const renderSuccess = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      <div className="success-checkmark" />
      <p className="text-lg font-semibold text-green-600">
        Successfully created kiosk attractor
      </p>
      {blobUrl && (
        <KioskAttractorDisplayPreview
          currentAttractors={[{ imageUrl: blobUrl }]}
          blobIsVideo={fileType === "video"}
        />
      )}
    </div>
  );

  // Render the error screen
  const renderError = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      <p className="text-lg font-semibold text-red-600 mb-4">{error}</p>
      {blobUrl && (
        <KioskAttractorDisplayPreview
          currentAttractors={[{ imageUrl: blobUrl }]}
          blobIsVideo={fileType === "video"}
        />
      )}
      <div className="flex flex-row gap-6 justify-center">
        <button
          onClick={() => {
            setError(""); // Clear error
            handleSubmit({ startDate: null, endDate: null, selected: null }); // Retry with existing data
          }}
          type="button"
          className="hover:opacity-80 w-32 rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
        >
          Retry
        </button>
        <button
          onClick={() => navigate("/kiosk-attractors")}
          type="button"
          className="w-32 rounded-md bg-gray-400 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <>
      <style>{loaderStyles}</style>
      {screenState === "form" && renderForm()}
      {screenState === "loading" && renderLoading()}
      {screenState === "success" && renderSuccess()}
      {screenState === "error" && renderError()}
    </>
  );
};

export default observer(AttractorCreator);
